import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import { mountRetentionDashboard } from './retentionAnalytics/retentionDashboardEngine';

const STILL_LOADING = /still loading/i;
const RETRY_MS = 30000;
const MAX_RETRIES = 12;

const loadPreview = (campaignId) =>
    axios
        .get(`/api/v1/retention-analytics/campaigns/${encodeURIComponent(campaignId)}/preview`)
        .then((res) => res.data.data)
        .catch((err) => {
            throw new Error(err.response?.data?.error || err.message || 'Preview unavailable');
        });

const KlaviyoAnalytics = () => {
    const hostRef = useRef(null);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const retries = useRef(0);

    const load = useCallback(() => {
        axios
            .get('/api/v1/retention-analytics/dashboard')
            .then((res) => {
                setData(res.data.data);
                setError(null);
            })
            .catch((err) => setError(err.response?.data?.error || err.message || 'Unable to load retention analytics'));
    }, []);

    useEffect(load, [load]);

    useEffect(() => {
        if (!data || !(data.warnings || []).some((w) => STILL_LOADING.test(w))) return undefined;
        let cancelled = false;
        let timer;
        const poll = () => {
            timer = setTimeout(() => {
                retries.current += 1;
                axios
                    .get('/api/v1/retention-analytics/dashboard')
                    .then((res) => {
                        if (cancelled) return;
                        const next = res.data.data;
                        const ready = !(next.warnings || []).some((w) => STILL_LOADING.test(w));
                        if (ready || retries.current >= MAX_RETRIES) setData(next);
                        else poll();
                    })
                    .catch(() => {
                        if (!cancelled && retries.current < MAX_RETRIES) poll();
                    });
            }, RETRY_MS);
        };
        poll();
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [data]);

    useEffect(() => {
        if (!data || !hostRef.current) return undefined;
        return mountRetentionDashboard(hostRef.current, { data, loadPreview });
    }, [data]);

    if (error) {
        return (
            <div className="p-6">
                <h1 className="text-2xl font-semibold text-gray-900">Retention Analytics</h1>
                <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <LoaderSpinner />
            </div>
        );
    }
    return <div ref={hostRef} data-testid="retention-dashboard" />;
};

export default KlaviyoAnalytics;
